/**
 * M9 CH-14a: finding seeded hulks (plan `docs/plans/2026-10-05-m9-claimable-hulls.md` H-9, §3.3, card CH-14a). The
 * deep-scan ping (`REV.scanPing`, `exploration/scanner.ts`) finds every seeded First Rush hulk (`hulksOf`, `hk:` keys)
 * of the current system within the scanner's radius (0 = the whole system), like it reveals POIs. A found hulk is
 * recorded as `career.hulks.sites[key].found` (CH-2b), so it stays a nav contact on every later entry; the nearest
 * newly found one becomes the cruise target through the Reach's `setNavTarget` (kind `hulk`, `reach/navPoints.ts`),
 * the path the Logs screen's leads use (D-430), unless the pilot already holds a target or a route.
 *
 * Off-screen hulks (`ow-` keys, CH-3b) are not found by the ping: they surface on the traffic manifest where the loss
 * happened. Pure, Career in, Career out; a ping is rare, so this allocates freely and never runs per frame.
 */
import type { Career, HulkSite, HulkState } from '../career/types'
import type { NavPoint } from '../reach/navPoints'
import { hulksConfig, type HulksConfig } from './data'
import type { HulkSpot } from './place'

/** The hulks of `spots` within `radiusM` (0 or less = the whole system) of (x, y, z), nearest first. */
export function hulksInPing(spots: readonly HulkSpot[], x: number, y: number, z: number, radiusM: number): HulkSpot[] {
  const r2 = radiusM > 0 ? radiusM * radiusM : Number.POSITIVE_INFINITY
  const out: { spot: HulkSpot; d2: number }[] = []
  for (const s of spots) {
    const dx = s.position[0] - x
    const dy = s.position[1] - y
    const dz = s.position[2] - z
    const d2 = dx * dx + dy * dy + dz * dz
    if (d2 <= r2) out.push({ spot: s, d2 })
  }
  out.sort((a, b) => a.d2 - b.d2 || (a.spot.key < b.spot.key ? -1 : 1))
  return out.map((o) => o.spot)
}

/** True when the Career lists `key` as found (or surveyed, which implies it was found). */
export function hulkFound(career: Career, key: string): boolean {
  const site = career.hulks?.sites[key]
  return site !== undefined && (site.found === true || site.surveyed !== undefined)
}

/** The result of a ping: the Career with the new finds, and the newly found hulks, nearest first. */
export interface HulkFinds {
  readonly career: Career
  readonly found: readonly HulkSpot[]
}

/**
 * Marks every hulk of `inRange` (nearest first) the Career has not found, finished (`done`) or surveyed as
 * `found`. The same Career object when nothing is new.
 */
export function markHulksFound(career: Career, inRange: readonly HulkSpot[]): HulkFinds {
  const state: HulkState = career.hulks ?? { done: [], sites: {} }
  const fresh = inRange.filter((s) => !state.done.includes(s.key) && !hulkFound(career, s.key))
  if (fresh.length === 0) return { career, found: [] }
  const sites: Record<string, HulkSite> = { ...state.sites }
  for (const s of fresh) sites[s.key] = { ...sites[s.key], hull: s.hull, found: true }
  return { career: { ...career, hulks: { done: state.done, sites } }, found: fresh }
}

/** "Hulk · Interceptor": a hulk's nav-contact name, with the survey card's hull names (`hulks.json text.hulls`). */
export function hulkNavName(hull: string, cfg: HulksConfig = hulksConfig()): string {
  return `Hulk · ${cfg.text.hulls[hull] ?? hull}`
}

/** The nav contacts of a system: its hulks the Career has found and not finished, as `hulk` nav points. */
export function hulkNavPoints(spots: readonly HulkSpot[], career: Career, cfg: HulksConfig = hulksConfig()): NavPoint[] {
  const done = career.hulks?.done ?? []
  const out: NavPoint[] = []
  for (const s of spots) {
    if (done.includes(s.key) || !hulkFound(career, s.key)) continue
    out.push({ id: s.key, name: hulkNavName(s.hull, cfg), position: s.position })
  }
  return out
}

/** Whether a ping's first find may become the cruise target: only when the pilot holds no target and no route. */
export function mayTargetFind(state: { readonly navTarget: unknown; readonly route: unknown }): boolean {
  return state.navTarget === null && state.route === null
}
